import AuthenticationController from '../services/Auth/AuthenticationController';
import SystemSettingsController from '@/services/Admin/SystemSettingsController';
import { isEmpty } from 'lodash';
import * as TYPES from './types';
import { cancelAll, setCancelToken } from '../plugins/http';
import swal from 'sweetalert2';

export default {

    setFetching({ commit }, obj) {
        commit(TYPES.MAIN_SET_FETCHING, obj);
    },

    setMessage({ commit }, obj) {
        commit(TYPES.MAIN_SET_MESSAGE, obj);

        if (!(obj.type === 'validation' || obj.stay)) {
            setTimeout(function () {
                commit(TYPES.MAIN_SET_MESSAGE, { type: 'success', message: '' });
                commit(TYPES.MAIN_SET_MESSAGE, { type: 'error', message: [] });
                commit(TYPES.MAIN_SET_MESSAGE, { type: 'warning', message: '' });
                commit(TYPES.MAIN_SET_MESSAGE, { type: 'validation', message: {} });
            }, obj.deleteTime ? obj.deleteTime : 15000);
        }
    },

    resetMessages({ commit }) {
        commit(TYPES.MAIN_SET_MESSAGE, { type: 'success', message: '' });
        commit(TYPES.MAIN_SET_MESSAGE, { type: 'error', message: [] });
        commit(TYPES.MAIN_SET_MESSAGE, { type: 'warning', message: '' });
        commit(TYPES.MAIN_SET_MESSAGE, { type: 'validation', message: {} });
    },

    dismissMessage({ commit }, type) {
        let obj;

        if (type === 'error') {
            obj = {type: type, message: []};
        } else if (type === 'validation') {
            obj = {type: type, message: {}};
        } else {
            // success, warning
            obj = {type: type, message: ''};
        }

        commit(TYPES.MAIN_SET_MESSAGE, obj);
    },

    setAuthUser({ commit }, user) {
        commit(TYPES.AUTH_SET_USER, user);

        return Promise.resolve(user);
    },

    setUserVersion({ commit }, version) {
        commit(TYPES.AUTH_SET_USER_VERSION, version);

        return Promise.resolve(version);
    },

    setUserAbilities({ commit }, abilities) {
        commit(TYPES.AUTH_SET_USER_ABILITIES, abilities);

        return Promise.resolve(abilities);
    },

    clearUserAbilities({ dispatch }) {
        dispatch('setUserAbilities', []);

        return Promise.resolve();
    },

    setProjectAbilities({ commit }, abilities) {
        commit(TYPES.AUTH_SET_PROJECT_ABILITIES, abilities);

        return Promise.resolve(abilities);
    },

    clearProjectAbilities({ dispatch }) {
        dispatch('setProjectAbilities', []);

        return Promise.resolve();
    },

    loadUserSettings({ dispatch }) {
        return AuthenticationController.userSettings()
            .then(settings => dispatch('setUserSettings', settings))
            .catch(() => {
                dispatch('setUserSettings', {});
                return Promise.reject('FAIL_IN_LOAD_SETTINGS');
            });
    },

    setUserSettings({ commit }, settings) {
        commit(TYPES.AUTH_SET_USER_SETTINGS, settings);

        return Promise.resolve(settings);
    },

    clearUserSettings({ dispatch }) {
        dispatch('setUserSettings', {});

        return Promise.resolve();
    },

    logoutAuthUser({ dispatch }) {
        // cancel all existing requests
        cancelAll();
        // create a new cancel token
        setCancelToken();

        return AuthenticationController.logout().then(() => {
            return Promise.all([
                dispatch('setAuthUser', {}),
                dispatch('clearUserSettings'),
                dispatch('clearSidebarLogoUrl'),
                dispatch('clearIsGlobalManagementSystem'),
                dispatch('clearHasClOnlyFeatures'),
                dispatch('clearUserAbilities'),
                dispatch('clearProjectAbilities')
            ]);
        });
    },

    checkUserAvailable({ dispatch, state }) {
        if (!isEmpty(state.auth.user)) {
            if (state.auth.user_version) {
                // Asynchronous to avoid wait as there's no need to reload in most cases
                AuthenticationController.getSystemVersion()
                    .then(version => {
                        if (version !== state.auth.user_version) {
                            window.location.reload(true);
                        }

                        return Promise.resolve(state.auth.user);
                    });
            }
            return Promise.resolve(state.auth.user);
        }

        return dispatch('loadAuthUser');
    },

    loadAuthUser({ dispatch }) {
        return AuthenticationController.authUser()
            .then(user => {
                if (isEmpty(user)) {
                    dispatch('setAuthUser', {});
                    return Promise.reject('FAIL_IN_LOAD_USER');
                }

                return Promise.all([
                    dispatch('setAuthUser', user),

                    AuthenticationController.userAbilities()
                        .then(abilities => {
                            dispatch('setUserAbilities', abilities);
                        })
                        .catch(() => {
                            dispatch('setUserAbilities', []);
                            return Promise.reject('FAIL_IN_LOAD_ABILITIES');
                        }),

                    AuthenticationController.projectAbilities()
                        .then(abilities => {
                            dispatch('setProjectAbilities', abilities);
                        })
                        .catch(() => {
                            dispatch('setProjectAbilities', []);
                            return Promise.reject('FAIL_IN_LOAD_PROJECT_ABILITIES');
                        }),

                    AuthenticationController.getSystemVersion()
                        .then(version => {
                            dispatch('setUserVersion', version);
                        }),

                    dispatch('loadUserSettings')
                ]);
            });
    },

    reloadUserAbilities({ dispatch }) {
        AuthenticationController.userAbilities()
            .then(abilities => {
                dispatch('setUserAbilities', abilities);
            })
            .catch(() => {
                dispatch('setUserAbilities', []);
                return Promise.reject('FAIL_IN_LOAD_ABILITIES');
            });
    },

    reloadProjectAbilities({ dispatch }) {
        AuthenticationController.projectAbilities()
            .then(abilities => {
                dispatch('setProjectAbilities', abilities);
            })
            .catch(() => {
                dispatch('setProjectAbilities', []);
                return Promise.reject('FAIL_IN_LOAD_PROJECT_ABILITIES');
            });
    },

    /**
     * WARNING:
     * Project abilities are only checked like "userCanInAnyProject". So when using them, don't rely
     * on the route ability check but make sure to check properly after loading the requested page.
     */
    checkRouteAbility({ dispatch, state }, ability) {
        // Check for company ability
        if (state.auth.abilities.indexOf('*') >= 0
            || state.auth.abilities.indexOf(ability) >= 0
        ) {
            return Promise.resolve();
        }

        // If not a company ability, check for project ability
        for (const projectKey in state.auth.project_abilities) {
            if (state.auth.project_abilities[projectKey].indexOf(ability) >= 0) {
                return Promise.resolve();
            }
        }

        return Promise.reject('FAIL_IN_ROUTE_ABILITY');
    },

    setSidebarLogoUrl({ commit }, logoUrl) {
        commit(TYPES.MAIN_SET_SIDEBAR_LOGO_URL, logoUrl);

        return Promise.resolve(logoUrl);
    },

    loadSidebarLogoUrl({ dispatch, state }, forceReload = false) {
        if (state.sidebar_logo_url && !forceReload) {
            return state.sidebar_logo_url;
        }

        SystemSettingsController.sidebarLogoUrl()
            .then(logoUrl => dispatch('setSidebarLogoUrl', logoUrl));
    },

    clearSidebarLogoUrl({ dispatch }) {
        dispatch('setSidebarLogoUrl', null);

        return Promise.resolve();
    },

    setIsGlobalManagementSystem({ commit }, isGlobalManagementSystem) {
        commit(TYPES.MAIN_SET_IS_GLOBAL_MANAGEMENT_SYSTEM, isGlobalManagementSystem);

        return Promise.resolve(isGlobalManagementSystem);
    },

    loadIsGlobalManagementSystem({ dispatch, state }) {
        if (state.is_global_management_system !== null) {
            return state.is_global_management_system;
        }

        SystemSettingsController.getConfigurationSettings()
            .then(response => {
                dispatch('setIsGlobalManagementSystem', response.isManagementSystem);
            });
    },

    clearIsGlobalManagementSystem({ dispatch }) {
        dispatch('setIsGlobalManagementSystem', null);

        return Promise.resolve();
    },

    setHasClOnlyFeatures({ commit }, hasClOnlyFeatures) {
        commit(TYPES.MAIN_SET_HAS_CL_ONLY_FEATURES, hasClOnlyFeatures);

        return Promise.resolve(hasClOnlyFeatures);
    },

    loadHasClOnlyFeatures({ dispatch, state }) {
        if (state.has_cl_only_features !== null) {
            return state.has_cl_only_features;
        }

        SystemSettingsController.getConfigurationSettings()
            .then(response => {
                dispatch('setHasClOnlyFeatures', response.hasClOnlyFeatures);
            });
    },

    clearHasClOnlyFeatures({ dispatch }) {
        dispatch('setHasClOnlyFeatures', null);

        return Promise.resolve();
    },

    swalToast({}, settings) {
        let customClass = '';
        let timer = settings.timer ? settings.timer : 15000;
        let animation = true;

        if (settings.message.constructor === Array) {
            settings.message = settings.message.join(' ');
        }

        if (settings.stay) {
            timer = null;
        }

        if (settings.animation === false) {
            animation = false;
        }

        switch (settings.type) {
            case 'success':
                customClass = 'greenBoxShadow';
                break;
            case 'warning':
                customClass = 'orangeBoxShadow';
                break;
            case 'error':
                customClass = 'redBoxShadow';
                break;
            case 'info':
                customClass = 'blueBoxShadow';
                break;
        }
        swal({
            type: settings.type,
            toast: true,
            title: settings.message,
            showConfirmButton: false,
            timer: timer,
            position: 'top-end',
            customClass: customClass,
            animation: animation
        });
    },

    checkSystemLocale({ dispatch, state }) {
        if (state.system_locale !== null) {
            return state.system_locale;
        }

        return dispatch('loadSystemLocale');
    },

    loadSystemLocale({ dispatch }) {
        return AuthenticationController.getSystemLocale()
            .then(locale => {
                return dispatch('setSystemLocale', locale);
            });
    },

    setSystemLocale({ commit }, systemLocale) {
        commit(TYPES.MAIN_SET_SYSTEM_LOCALE, systemLocale);

        return Promise.resolve(systemLocale);
    },

    setLatestScrollToComment({ commit }, latestScrollToComment) {
        commit(TYPES.MAIN_SET_LATEST_SCROLL_TO_COMMENT, latestScrollToComment);

        return Promise.resolve(latestScrollToComment);
    },

  setPasswordResetEnabled({ commit }, passwordResetEnabled) {
      let value = parseInt(passwordResetEnabled) === 1;

      commit(TYPES.MAIN_SET_PASSWORD_RESET_ENABLED, value);

      return Promise.resolve(value);
  },

  async loadPasswordResetEnabled({ dispatch, state }) {
      if (state.password_reset_enabled !== null) {
          return state.password_reset_enabled;
      }

      let setting = await SystemSettingsController.getPasswordResetSetting();

      dispatch('setPasswordResetEnabled', setting.data);
  },
};



// WEBPACK FOOTER //
// ./src/store/actions.js